import {
  Concept,
  Pack,
  PackAuthor,
  PackConfig,
  PackLicense,
  PackSource,
  RecursivePartial,
} from "../Types";
import { PackInfoEditor, PackInfoView } from "./PackInfoView";
import ColumnSelector from "./ColumnSelector";
import DropZoneView from "./DropZoneView";
import { PackAttachment } from "../Types";
import RepView from "./RepView";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditableText } from "./EditableText";
import { uuid } from "../Lib";
import AISuggestor from "./AISuggestor";
import { MultiFieldEditor } from "./MultiFieldEditor";
import { VirtuosoHandle } from "react-virtuoso";
import { AllConceptsView } from "./AllConceptsView";
import { AttributionEditor } from "./AttributionEditor";
import { PackEditorCallbacks } from "../core/EditorTypes";
import { PackConfigPage } from "../pages/PackConfigPage";

// import AISuggestor from "./AISuggestor";

type EditorProps = {
  pack: Pack;
  packAttachments: PackAttachment[];
  packEditorCallbacks: PackEditorCallbacks;

  reset: any;
  save: any;
  upload: any;
  networkState: "idle" | "connecting" | "connected";
};

type EditorState = {
  route: "index" | "concepts" | "attachments" | "pack" | "attribution";
};

const visibleColumns = [
  "text",
  "subtext",
  "image",
  "audio",
  "video",
  "ttsText",
  // "html",
  // "markdown",
];

const TTS_LANGUAGES = [
  "en-US",
  "en-GB",
];

export default function Editor(
  props: EditorProps,
) {
  const {
    pack,
    packAttachments,
    packEditorCallbacks,
    ...otherProps
  } = props;

  const {
    addConceptCb,
    addBaseRepCb,
    setConceptsCb,
    updateRepField,

    addTemplate,
    addPackField,
    updateConceptField,
    updatePackField,
    editPackField,
    removePackField,
    removeConceptField,
    renamePackField,
    renameTemplate,

    editTemplate,
    removeTemplate,

    addLicense,
    editLicense,
    removeLicense,
    addAuthor,
    editAuthor,
    removeAuthor,
    addSource,
    editSource,
    removeSource,

    updateBaseRepKey,
    removeBaseRepCb,
    onPackAttachment,
    updatePackAttachmentName,
    removePackAttachment,
  } = packEditorCallbacks;

  // get suggested AI concepts from network
  const [suggestedNewConcepts, setSuggestedNewConcepts] = useState<any[]>([]);
  const [filledExistingConcepts, setFilledExistingConcepts] = useState<any[]>(
    [],
  );

  const mergedConcepts = pack.data.concepts.map((concept) => {
    const filledConcept = filledExistingConcepts.find(
      (filledConcept) => filledConcept.id === concept.id,
    );
    if (filledConcept) {
      return filledConcept;
    }
    return concept;
  });

  const concepts = [...mergedConcepts, ...suggestedNewConcepts];

  const [selection, _setSelection] = useState([-1, -1, -1]);

  const scrollRef = useRef<VirtuosoHandle>(null);

  const addConceptCbWrapper = useCallback(() => {
    addConceptCb();
    setTimeout(() =>
      scrollRef.current?.scrollTo({
        top: 9999999,
      })
    );
  }, []);

  const getSelectedElement = useCallback(
    (conceptIndex: number, repIndex: number, repKeyIndex: number) => {
      return;
    },
    [],
  );

  const setSelection = useCallback(
    (conceptIndex: number, repIndex: number, repKeyIndex) => {
      if (
        selection[0] != conceptIndex || selection[1] != repIndex ||
        selection[2] != repKeyIndex
      ) {
        // console.log("updating state to be", [
        //   conceptIndex,
        //   repIndex,
        //   repKeyIndex,
        // ]);

        _setSelection([conceptIndex, repIndex, repKeyIndex]);
      }
    },
    [],
  );

  useEffect(() => {
    document.onkeydown = (event: KeyboardEvent) => {
      let newSelection = [...selection];
      if (selection[0] === -1) {
        newSelection = [0, 0, 0];
      }
      const maxRepKeys = 2;
      switch (event.key) {
        case "ArrowUp":
          event.stopPropagation();
          event.preventDefault();
          newSelection[1]--;
          break;
        case "ArrowDown":
          event.stopPropagation();
          event.preventDefault();
          newSelection[1]++;
          break;
        // case 39: // Right arrow
        //   newSelection[2]++;
        //   break;
        // case 37: // Left arrow
        //   newSelection[2]--;
        //   break;
        case " ":
          if (newSelection[0] === concepts.length) {
            event.stopPropagation();
            event.preventDefault();
            addConceptCbWrapper();
          }
          return;
        case "Enter":
          if (newSelection[0] === concepts.length) {
            event.stopPropagation();
            event.preventDefault();
            addConceptCbWrapper();
          }
          return;
        default:
          // console.log(event.keyCode);
          return;
      }

      // Roll over
      if ((newSelection[0] > 0 || newSelection[1] > 0) && newSelection[2] < 0) {
        // Roll over left, previous rep row
        newSelection[2] = maxRepKeys - 1;
        newSelection[1]--;
      } else if (newSelection[2] >= maxRepKeys) {
        // Roll over right, next rep row
        newSelection[2] = 0;
        newSelection[1]++;
      }
      newSelection[0] = Math.max(
        0,
        Math.min(newSelection[0], concepts.length),
      );

      const repsForConcept = newSelection[0] < concepts.length
        ? Object.keys(concepts[newSelection[0]].reps).length
        : 0;
      if (newSelection[1] < 0) {
        newSelection[0]--;
        newSelection[0] = Math.max(
          0,
          Math.min(newSelection[0], concepts.length),
        );
        const repsForNewConcept = newSelection[0] < concepts.length
          ? Object.keys(concepts[newSelection[0]].reps).length
          : 0;
        // Roll over up, previous concept
        newSelection[1] = repsForNewConcept - 1;
      } else if (
        newSelection[1] > repsForConcept /* intentionally +1 for id slot */
      ) {
        newSelection[1] = 0;
        newSelection[0]++;
      }

      newSelection[0] = Math.max(
        0,
        Math.min(newSelection[0], concepts.length),
      );
      setSelection(newSelection[0], newSelection[1], newSelection[2]);
    };
  }, [selection, concepts, addConceptCb, setSelection]);

  const [selectedCols, setSelectedCols] = useState([
    "text",
    "image",
    "audio",
    "examples",
    // "attachments",
    // "controls",
  ]);

  const updateRepFieldChild = useCallback(
    (repKey: string, fieldKey: string, value: any) => {
      updateRepField(null, repKey, fieldKey, value);
    },
    [updateRepField],
  );

  const selectedColsNonControls = useMemo(
    () =>
      selectedCols.map((x) => x === "text-to-speech" ? "ttsText" : x).filter(
        (x) => visibleColumns.includes(x),
      ),
    [selectedCols],
  );

  const conceptCards = concepts.map(
    (c, conceptIndex) => {
      return {};
    },
  );

  function itemSize(index) {
    return 1000;
  }

  const [editorState, setEditorState] = useState<EditorState>({
    route: "index",
  });

  const goToIndex = useCallback(() => {
    setEditorState({
      route: "index",
    });
  }, []);

  // no index route
  const routeButtons = [{
    title: "Cards",
    desc: "View and edit cards in pack.",
    icon: "note-icon@2x",
    route: "concepts",
    onClick: useCallback(() => {
      setEditorState({
        route: "concepts",
      });
    }, []),
  }, {
    title: "Attachments",
    desc: "Manage attached sounds and images.",
    icon: "image-icon@2x",
    route: "attachments",
    onClick: useCallback(() => {
      setEditorState({
        route: "attachments",
      });
    }, []),
  }, {
    title: "Pack",
    desc: "Configure fields and formatting.",
    icon: "package-icon@2x",
    route: "pack",
    onClick: useCallback(() => {
      setEditorState({
        route: "pack",
      });
    }, []),
  }, {
    title: "Attribution",
    desc: "Manage license, authors, and sources.",
    icon: "book-icon@2x",
    route: "pack",
    onClick: useCallback(() => {
      setEditorState({
        route: "attribution",
      });
    }, []),
  }].map((x) => {
    return (
      <div className="infocard clickable" onClick={x.onClick}>
        <div className="infocard-header">
          <div className="infocard-header-icon">
            <img src={`/icons/${x.icon}.png`} alt={`${x.title} icon`} />
          </div>
          <div className="infocard-header-text">
            {x.title}
          </div>
        </div>
        <div className="infocard-body">
          {x.desc}
        </div>
      </div>
    );
  });

  const repKeys = Object.keys(pack.data.reps);
  const repCards = repKeys.map((repKey, repIndex) => {
    const rep = pack.data.reps[repKey];
    return (
      <RepView
        fields={[
          "promptText",
          "promptTextFull",
          "promptColor",
          ...selectedColsNonControls,
        ]}
        onEditKey={(str) => {
          updateBaseRepKey(repKey, str);
        }}
        // key={repKey}
        key={`${repIndex}`}
        rep={rep}
        name={repKey}
        fullName={undefined}
        selected={false}
        selection={[-1, -1, -1]}
        updateRepField={updateRepFieldChild}
        showPlaceholder={true}
        removeRepCb={removeBaseRepCb}
        setSelection={(id) => {
          setSelection(-1, -1, -1);
        }}
        isAiSuggestion={false}
        attachments={packAttachments}
      />
    );
  });

  let repPairs: [string, string, string][] = [];

  // generate all possible pairs of repKeys
  for (let i = 0; i < repKeys.length; i++) {
    for (let j = 0; j < repKeys.length; j++) {
      if (
        i !== j &&
        !repPairs.find((x) =>
          (x[0] === repKeys[i] && x[2] === repKeys[j]) ||
          (x[0] === repKeys[j] && x[2] === repKeys[i])
        )
      ) {
        const currentWire = pack.data.wiring.find((x) =>
          (x.startsWith(repKeys[i]) || x.startsWith(repKeys[j])) &&
          (x.endsWith(repKeys[i]) || x.endsWith(repKeys[j]))
        );
        let connection = currentWire
          ? currentWire.includes("<>") ? "<>" : ""
          : "";
        if (connection === "" && currentWire) {
          if (
            currentWire.startsWith(`${repKeys[i]}<`) ||
            currentWire.startsWith(`${repKeys[i]}>`)
          ) {
            connection = ">";
          } else {
            connection = "<";
          }
        }

        repPairs.push([repKeys[i], connection, repKeys[j]]);
      }
    }
  }

  if (editorState.route === "index") {
    return (
      <div>
        <PackInfoEditor
          pack={pack}
          updatePackField={updatePackField}
          packEditorCallbacks={packEditorCallbacks}
        />
        <div className="infocard-container padding">
          {routeButtons}
        </div>
        <PackInfoView pack={pack} />
      </div>
    );
  } else if (editorState.route === "concepts") {
    return (
      <div>
        <div className="back-button padding" onClick={goToIndex}>
          ← <span className="underline">back to pack</span>
        </div>
        <div className="section">🔍 choose what to view</div>
        <div style={{ padding: "0 2em" }}>
          <ColumnSelector
            columns={[
              "text",
              // "html",
              // "markdown",
              "subtext",
              "audio",
              "video",
              "image",
              // "attachments",
              "text-to-speech",
              // "templates",
              // "fields",
              // "controls",
            ]}
            selectedColumns={selectedCols}
            setSelected={setSelectedCols}
          />
        </div>
        <AllConceptsView
          scrollRef={scrollRef}
          concepts={concepts}
          addConceptCbWrapper={addConceptCbWrapper}
          selection={selection}
          pack={pack}
          getSelectedElement={getSelectedElement}
          setSelection={setSelection}
          selectedColsNonControls={selectedColsNonControls}
          updateRepField={updateRepField}
          packEditorCallbacks={packEditorCallbacks}
          packAttachments={packAttachments}
          {...otherProps}
        />
        &nbsp;
        {
          // Check if url has ?aienabled=true
          window.location.search.includes("ai") && (
            <AISuggestor
              pack={pack}
              suggestedNewConcepts={suggestedNewConcepts}
              setSuggestedNewConcepts={setSuggestedNewConcepts}
              filledExistingConcepts={filledExistingConcepts}
              setFilledExistingConcepts={setFilledExistingConcepts}
              setConcepts={setConceptsCb}
            />
          )
        }
      </div>
    );
  } else if (editorState.route === "attachments") {
    return (
      <div style={{ paddingBottom: "2em" }}>
        <div className="back-button padding" onClick={goToIndex}>
          ← <span className="underline">back to pack</span>
        </div>
        <div className="section">📂 attachments</div>
        <DropZoneView
          onFileEvent={() => {}}
          onFile={function (...params: any[]) {
            if (
              params[2] === "application/json" ||
              params[2] === "application/zip" ||
              params[2] === "application/x-zip-compressed"
            ) {
              return;
            }
            console.log(params);
            onPackAttachment({
              data: params[0],
              name: params[1],
              type: params[2],
              id: uuid(),
            });
          }}
          attach={true}
        />
        {packAttachments.map((a) => {
          // console.log('attachment is', a)
          return (
            <div style={{ display: "inline-block" }}>
              {/* {a.name} ({((a.data as any).length / 1024).toFixed(1)}KB) */}
              <EditableText
                write={(text: string) => {
                  updatePackAttachmentName(a.id, text);
                }}
                value={a.name}
              />
              <span style={{ paddingLeft: "2em", opacity: 0.4 }}>
                <button
                  onClick={() => {
                    removePackAttachment(a.id);
                  }}
                >
                  🆇
                </button>
                ({(((a.data as any).length ?? ((a.data as any).size)) / 1024)
                  .toFixed(1)}KB)
              </span>
            </div>
          );
        })}
      </div>
    );
  } else if (editorState.route === "pack") {
    return (
      <PackConfigPage
        goToIndex={goToIndex}
        pack={pack}
        selectedCols={selectedCols}
        setSelectedCols={setSelectedCols}
        repCards={repCards}
        repPairs={repPairs}
        repKeys={repKeys}
        packEditorCallbacks={packEditorCallbacks}
      />
    );
  } else if (editorState.route === "attribution") {
    return (
      <>
        <div className="back-button padding" onClick={goToIndex}>
          ← <span className="underline">back to pack</span>
        </div>
        <div className="section">📝 attribution</div>
        <div className="attribution padding">
          <AttributionEditor
            attribution={pack.extra?.attribution || {}}
            onAddAuthor={addAuthor}
            onRemoveAuthor={removeAuthor}
            onEditAuthor={editAuthor}
            onAddLicense={addLicense}
            onRemoveLicense={removeLicense}
            onEditLicense={editLicense}
            onAddSource={addSource}
            onRemoveSource={removeSource}
            onEditSource={editSource}
          />
        </div>
      </>
    );
  } else return null;
}
